/*
 * This module provides functions to log more convenient that just writing
 * to console.
 * 
 * The convenience comes from having names that better explain the purpose,
 * so they can be easily searched for uses. Also it allows better suppress
 * them in production, etc.
 * 
 */

let showInfo = true;
let showVerbose = true;
let showProgrammingError = true;

/**
 * Logs some traceable information that can be useful even in production.
 * 
 * The message has its data formated.
 * 
 * @param module Origin of the log message
 * @param msg Message, with format marks
 * @param data Object containing values for formatting and additional info
 */
export function info(module: string, msg: string, data?: Record<string,any>) {
    if(showInfo) {
        let [ m, d ] = composeContent(msg, data);
        writeLog(module, m, d);
    }
}

/**
 * Logs information that it is too may information for production.
 * 
 * The message has its data formated.
 * 
 * @param module Origin of the log message
 * @param msg Message, with format marks
 * @param data Object containing values for formatting and additional info
 */
export function verbose(module: string, msg: string, data?: Record<string,any>) {
    if(showVerbose) {
        let [ m, d ] = composeContent(msg, data);
        writeLog(module, m, d);
    }
}

/**
 * Logs information that it is useful just while developing.
 * 
 * This messsages should always be removed before a production release.
 * 
 * @param module Origin of the log message
 * @param msg Message, with format marks
 * @param data Object containing values for formatting and additional info
 */
export function devlog(module: string, msg: string, data?: Record<string,any>) {
    if(showVerbose) {
        let [ m, d ] = composeContent(msg, data);
        writeDebug(module, m, d);
    }
}

/**
* Logs information that it is useful just while developing, adding the stack.
* 
* This messsages should always be removed before a production release.
* 
* @param module Origin of the log message
* @param msg Message, with format marks
* @param data Object containing values for formatting and additional info
*/
export function devtrace(module: string, msg: string, data?: Record<string,any>) {
   if(showVerbose) {
       let [ m, d ] = composeContent(msg, data);
       writeTrace(module, m, d);
   }
}

/**
 * Logs a situation that it is clearly a programming error (not a runtime
 * problem, not a user program).
 * 
 * The message has its data formated.
 * 
 * @param module Origin of the log message
 * @param msg Message, with format marks
 * @param data Object containing values for formatting and additional info
 */
export function programmingError(module: string, msg: string, data?: Record<string,any>): never {
    let [ m, d ] = composeContent(msg, data);
    if(showProgrammingError) {
        writeTrace(module, m, d);
    }
    throw new Error(m);
}

/**
 * Creates the message from the common data. It adds the module, formats a message
 * with the supplied data and adds any additional data. 
 * 
 * The message has its data formated.
 * 
 */
export function composeContent(msg: string, data?: Record<string,any>): [ string, any|null ] {
    
    let dataInMsg = new Set();
    let outMsg: string;
    let outData: any|null = null;

    let messageParts = msg.split(/(\$\{[A-Za-z0-9]+>?\})/g);
    
    if(messageParts) {
        const outParts = messageParts.map(part => {
            if(part.substring(0,2) === '${' && part.substring(part.length - 1) === '}') {
                let propertyName = part.substring(2, part.length - 1);
                if(propertyName[propertyName.length - 1] === '>') {
                    propertyName = propertyName.substring(0, propertyName.length);
                } else {
                    dataInMsg.add(propertyName);
                }
                return data ? data[propertyName] : '?unknown?';
            } else {
                return part;
            }
        });
        outMsg = outParts.join('');
    } else {
        outMsg = msg;
    }

    if(data) {
        const tmpOutData: any = {};
        let added = false;
        for(let pname of Object.getOwnPropertyNames(data)) {
            if(!dataInMsg.has(pname)) {
                tmpOutData[pname] = data[pname];
                added = true;
            }
        }
        if(added) {
            outData = tmpOutData;
        }
        
    }

    return [ outMsg, outData ];
}

/**
 * Does normal writing, for information messages
 * 
 * @param content What to log
 */
function writeLog(...content: unknown[]) {
    console.log(...content.filter(c => c !== null));
}

/**
 * Does debug writing
 * 
 * @param content What to log
 */
function writeDebug(...content: unknown[]) {
    console.debug(...content.filter(c => c !== null));
}

/**
 * Trace with calling stack
 * 
 * @param content What to log
 */
function writeTrace(...content: unknown[]) {
    console.trace(...content.filter(c => c !== null));
}

/**
 * Does writing as an error
 * 
 * @param content What to log
 */
function writeError(...content: unknown[]) {
    console.error(...content.filter(c => c !== null));
}